import React, { useRef, useState, useEffect, useMemo } from "react";
import SimpleMarkdown from "./SimpleMarkdown";

const SECTION_LABELS = {
  care_level_summary: "Care Level Summary",
  overview: "Overview",
  appearance: "Appearance",
  tank_requirements: "Tank Requirements",
  water_parameters: "Water Parameters",
  diet_feeding: "Diet & Feeding",
  temperament_tankmates: "Temperament & Tankmates",
  breeding: "Breeding",
  common_health_issues: "Common Health Issues",
};

const SummaryTable = ({ summary }) => {
  if (!summary) return null;

  const rows = [
    { label: "Care Level", value: summary.care_level, capitalize: true },
    { label: "Max Size", value: summary.max_size_inches ? `${summary.max_size_inches}"` : null },
    { label: "Min Tank Size", value: summary.min_tank_size_gallons ? `${summary.min_tank_size_gallons} gal` : null },
    { label: "Temperature", value: summary.temperature_range },
    { label: "pH", value: summary.ph_range },
    { label: "Reef Safe", value: summary.reef_safe },
    { label: "Plant Safe", value: summary.plant_safe },
    { label: "Diet", value: summary.diet },
    { label: "Temperament", value: summary.temperament },
    { label: "Lifespan", value: summary.lifespan },
  ].filter((r) => r.value);

  if (rows.length === 0) return null;

  return (
    <div className="care-guide-quick-facts bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
      <h3 className="font-bold text-gray-800 text-sm mb-3">Quick Facts</h3>
      <div className="flex flex-row flex-wrap gap-x-6 gap-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-row gap-1 text-sm">
            <span className="font-medium text-gray-600">{row.label}:</span>
            <span className={`text-gray-800 ${row.capitalize ? "capitalize" : ""}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AiDisclaimer = () => (
  <div className="mt-4 pt-3 border-t border-gray-200">
    <p className="text-xs text-gray-400 italic">
      This care guide was generated by AI and may contain inaccuracies. Always verify
      information with trusted sources before making decisions about your aquarium livestock.
    </p>
  </div>
);

const CareGuideContent = ({ summary, sections, scrollContainerRef }) => {
  const availableSections = useMemo(
    () => Object.entries(SECTION_LABELS).filter(([key]) => sections[key]),
    [sections]
  );

  const sectionRefs = useRef({});
  const tabsRef = useRef(null);
  const [activeTab, setActiveTab] = useState(availableSections[0]?.[0] || null);

  useEffect(() => {
    if (availableSections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveTab(visible[0].target.dataset.section);
        }
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin: "-80px 0px -60% 0px",
        threshold: 0.1,
      }
    );

    Object.keys(sectionRefs.current).forEach((key) => {
      const el = sectionRefs.current[key];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [availableSections, scrollContainerRef]);

  const scrollToSection = (key) => {
    const el = sectionRefs.current[key];
    if (!el) return;

    const tabsHeight = tabsRef.current?.offsetHeight || 48;

    if (scrollContainerRef?.current) {
      const container = scrollContainerRef.current;
      const containerTop = container.getBoundingClientRect().top;
      const elTop = el.getBoundingClientRect().top;
      const offset = container.scrollTop + (elTop - containerTop) - tabsHeight - 8;
      container.scrollTo({ top: offset, behavior: "smooth" });
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
    setActiveTab(key);
  };

  if (!sections) return null;

  return (
    <div className="care-guide-content">
      <SummaryTable summary={summary} />
      {availableSections.length > 1 && (
        <div
          ref={tabsRef}
          className="care-guide-tabs sticky top-0 z-10 bg-white border-b border-gray-200 mb-4 overflow-x-auto"
        >
          <div className="flex flex-row flex-wrap gap-1 py-2">
            {availableSections.map(([key, label]) => (
              <button
                key={key}
                onClick={() => scrollToSection(key)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeTab === key
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {availableSections.map(([key, label]) => (
        <div
          key={key}
          ref={(el) => (sectionRefs.current[key] = el)}
          data-section={key}
          className="care-guide-section mb-4"
        >
          <h2 className="text-lg font-bold text-gray-800 mt-3 mb-2">{label}</h2>
          <SimpleMarkdown content={sections[key]} />
        </div>
      ))}
      <AiDisclaimer />
    </div>
  );
};

export default CareGuideContent;
